import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { CellProps } from 'react-table';
import { getFreeBuildingsSummaryRequest } from '~/api/freeBuildingsSummary';
import CobuConstants from '~/helpers/cobuConstants';
import {
  CommentsFilter,
  EventsFilter,
  FreeBuildingSummaryAccessor,
  IFreeBuildingSummary
} from '~/types/freeBuildingsSummary';

const formatPercent = (value: number | null) =>
  value === null ? '--' : `${value}%`;

export const useFreeBuildingsSummary = () => {
  const [eventsFilter, setEventsFilter] = useState<EventsFilter>('all');
  const [commentsFilter, setCommentsFilter] = useState<CommentsFilter>('all');
  const [searchValue, setSearchValue] = useState('');

  const { data, isLoading } = useQuery(
    ['freeBuildingsSummary'],
    async () => {
      const res = await getFreeBuildingsSummaryRequest();
      return res.data;
    },
    { refetchOnWindowFocus: false }
  );

  const buildings = data || [];

  const filteredBuildings = useMemo(() => {
    return buildings.filter((building) => {
      const buildingName =
        building[FreeBuildingSummaryAccessor.buildingName] || '';
      if (
        searchValue.trim() &&
        !buildingName.toLowerCase().includes(searchValue.toLowerCase().trim())
      ) {
        return false;
      }

      const eventsCount =
        building[FreeBuildingSummaryAccessor.eventsCount90d];
      if (eventsFilter === 'has' && eventsCount <= 0) return false;
      if (eventsFilter === 'no' && eventsCount > 0) return false;

      const commentsCount =
        building[FreeBuildingSummaryAccessor.commentsCount30d];
      if (commentsFilter === 'has' && commentsCount <= 0) return false;
      if (commentsFilter === 'no' && commentsCount > 0) return false;

      return true;
    });
  }, [buildings, searchValue, eventsFilter, commentsFilter]);

  const summaryStats = useMemo(() => {
    const buildingCount = filteredBuildings.length;

    if (buildingCount === 0) {
      return {
        buildingCount: 0,
        avgAdoptionRate: null as number | null,
        avgActiveRate: null as number | null,
        totalEvents: 0,
        totalRSVPs: 0
      };
    }

    const buildingsWithAdoptionRate = filteredBuildings.filter(
      (b) => b[FreeBuildingSummaryAccessor.adoptionRate] !== null
    );
    const buildingsWithActiveRate = filteredBuildings.filter(
      (b) => b[FreeBuildingSummaryAccessor.activeRate] !== null
    );

    const sum = (values: number[]) =>
      values.reduce((total, value) => total + value, 0);

    return {
      buildingCount,
      avgAdoptionRate: buildingsWithAdoptionRate.length
        ? Math.round(
            sum(
              buildingsWithAdoptionRate.map(
                (b) => b[FreeBuildingSummaryAccessor.adoptionRate] as number
              )
            ) / buildingsWithAdoptionRate.length
          )
        : null,
      avgActiveRate: buildingsWithActiveRate.length
        ? Math.round(
            sum(
              buildingsWithActiveRate.map(
                (b) => b[FreeBuildingSummaryAccessor.activeRate] as number
              )
            ) / buildingsWithActiveRate.length
          )
        : null,
      totalEvents: sum(
        filteredBuildings.map(
          (b) => b[FreeBuildingSummaryAccessor.eventsCount90d]
        )
      ),
      totalRSVPs: sum(
        filteredBuildings.map(
          (b) => b[FreeBuildingSummaryAccessor.rsvpsCount30d]
        )
      )
    };
  }, [filteredBuildings]);

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Building name',
        accessor: FreeBuildingSummaryAccessor.buildingName,
        Cell: (cell: CellProps<IFreeBuildingSummary>) => (
          <a
            href={CobuConstants.navUrls.buildingDashboardLink(
              cell.row.original[FreeBuildingSummaryAccessor.buildingId]
            )}
            className='default-text'
          >
            {cell.row.original[FreeBuildingSummaryAccessor.buildingName]}
          </a>
        )
      },
      {
        Header: 'Occupied units',
        accessor: FreeBuildingSummaryAccessor.occupiedUnits
      },
      {
        Header: 'Total users (180d)',
        accessor: FreeBuildingSummaryAccessor.totalUsers180d
      },
      {
        Header: 'Adoption rate',
        accessor: FreeBuildingSummaryAccessor.adoptionRate,
        Cell: (cell: CellProps<IFreeBuildingSummary>) =>
          formatPercent(cell.value)
      },
      {
        Header: 'Monthly active users',
        accessor: FreeBuildingSummaryAccessor.monthlyActiveUsers
      },
      {
        Header: 'Active rate',
        accessor: FreeBuildingSummaryAccessor.activeRate,
        Cell: (cell: CellProps<IFreeBuildingSummary>) =>
          formatPercent(cell.value)
      },
      {
        Header: 'Events (90d)',
        accessor: FreeBuildingSummaryAccessor.eventsCount90d
      },
      {
        Header: 'RSVPs (30d)',
        accessor: FreeBuildingSummaryAccessor.rsvpsCount30d
      },
      {
        Header: 'Comments (30d)',
        accessor: FreeBuildingSummaryAccessor.commentsCount30d
      }
    ],
    []
  );

  return {
    loading: isLoading,
    buildings: filteredBuildings,
    tableColumns,
    summaryStats,
    eventsFilter,
    setEventsFilter,
    commentsFilter,
    setCommentsFilter,
    searchValue,
    setSearchValue
  };
};
